import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { CustomerUser, CustomerAuthContextType } from '../types/gallery';
import { galleryApiService } from '../services/galleryApiService';
import { useGalleryConfig } from './GalleryConfigContext';

interface AuthState {
  customer: CustomerUser | null;
  loading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_CUSTOMER'; payload: CustomerUser }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'LOGOUT' };

const initialState: AuthState = {
  customer: null,
  loading: false,
  error: null,
  isAuthenticated: false,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_CUSTOMER':
      return {
        ...state,
        customer: action.payload,
        isAuthenticated: true,
        loading: false,
        error: null,
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'LOGOUT':
      return {
        ...state,
        customer: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    default:
      return state;
  }
}

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

interface CustomerAuthProviderProps {
  children: ReactNode;
}

export const CustomerAuthProvider: React.FC<CustomerAuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const { config } = useGalleryConfig();

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      if (!config?.photographerId) return;

      try {
        dispatch({ type: 'SET_LOADING', payload: true });

        const token = localStorage.getItem(`gallery_token_${config.photographerId}`);
        if (!token) {
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }

        // Validate token and get customer data
        const customer = await galleryApiService.getCurrentCustomer(config.photographerId);
        dispatch({ type: 'SET_CUSTOMER', payload: customer });
      } catch {
        // Remove invalid token
        localStorage.removeItem(`gallery_token_${config.photographerId}`);
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkExistingSession();
  }, [config]);

  const login = async (email: string, password: string, remember = false) => {
    if (!config?.photographerId) {
      throw new Error('Gallery configuration not loaded');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await galleryApiService.customerLogin(config.photographerId, {
        email,
        password,
        remember,
      });

      // Store token
      const tokenKey = `gallery_token_${config.photographerId}`;
      if (remember) {
        localStorage.setItem(tokenKey, response.token);
      } else {
        sessionStorage.setItem(tokenKey, response.token);
      }

      dispatch({ type: 'SET_CUSTOMER', payload: response.customer });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Login failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const register = async (userData: {
    email: string;
    password: string;
    name: string;
    phone?: string;
  }) => {
    if (!config?.photographerId) {
      throw new Error('Gallery configuration not loaded');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await galleryApiService.customerRegister(config.photographerId, userData);

      // Store token
      const tokenKey = `gallery_token_${config.photographerId}`;
      localStorage.setItem(tokenKey, response.token);

      dispatch({ type: 'SET_CUSTOMER', payload: response.customer });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Registration failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const logout = async () => {
    if (!config?.photographerId) return;

    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Call logout endpoint
      await galleryApiService.customerLogout(config.photographerId);

      // Remove stored tokens
      const tokenKey = `gallery_token_${config.photographerId}`;
      localStorage.removeItem(tokenKey);
      sessionStorage.removeItem(tokenKey);

      dispatch({ type: 'LOGOUT' });
    } catch {
      // Force logout even if API call fails
      const tokenKey = `gallery_token_${config.photographerId}`;
      localStorage.removeItem(tokenKey);
      sessionStorage.removeItem(tokenKey);
      dispatch({ type: 'LOGOUT' });
    }
  };

  const updateProfile = async (updates: Partial<CustomerUser>) => {
    if (!config?.photographerId || !state.customer) {
      throw new Error('Not authenticated');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const updatedCustomer = await galleryApiService.updateCustomerProfile(
        config.photographerId,
        state.customer.id,
        updates
      );

      dispatch({ type: 'SET_CUSTOMER', payload: updatedCustomer });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Profile update failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const updatePreferences = async (preferences: Partial<CustomerUser['preferences']>) => {
    if (!config?.photographerId || !state.customer) {
      throw new Error('Not authenticated');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const updatedCustomer = await galleryApiService.updateCustomerPreferences(
        config.photographerId,
        state.customer.id,
        preferences
      );

      dispatch({ type: 'SET_CUSTOMER', payload: updatedCustomer });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Preferences update failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const resetPassword = async (email: string) => {
    if (!config?.photographerId) {
      throw new Error('Gallery configuration not loaded');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      await galleryApiService.requestPasswordReset(config.photographerId, email);
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password reset request failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const confirmResetPassword = async (token: string, newPassword: string) => {
    if (!config?.photographerId) {
      throw new Error('Gallery configuration not loaded');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      await galleryApiService.confirmPasswordReset(config.photographerId, token, newPassword);
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Password reset failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const resendVerification = async () => {
    if (!config?.photographerId || !state.customer) {
      throw new Error('Not authenticated');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      await galleryApiService.resendVerificationEmail(config.photographerId, state.customer.email);
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to resend verification';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const verifyEmail = async (token: string) => {
    if (!config?.photographerId) {
      throw new Error('Gallery configuration not loaded');
    }

    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const customer = await galleryApiService.verifyEmail(config.photographerId, token);
      dispatch({ type: 'SET_CUSTOMER', payload: customer });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Email verification failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      throw error;
    }
  };

  const value: CustomerAuthContextType = {
    customer: state.customer,
    loading: state.loading,
    error: state.error,
    isAuthenticated: state.isAuthenticated,
    login,
    register,
    logout,
    updateProfile,
    updatePreferences,
    resetPassword,
    confirmResetPassword,
    resendVerification,
    verifyEmail,
  };

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
};

export const useCustomerAuth = (): CustomerAuthContextType => {
  const context = useContext(CustomerAuthContext);
  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
